import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Download, Instagram, Music, Sparkles, RefreshCw, Upload, Video, Image as ImageIcon, Eye, EyeOff, Scissors, Play, X, Star, Library, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";

// Import background images
import smileCloseup from "@/assets/social-bg/smile-closeup.png";
import stretchPose from "@/assets/social-bg/stretch-pose.png";
import jawline from "@/assets/social-bg/jawline.png";
import lipsSunglasses from "@/assets/social-bg/lips-sunglasses.png";
import foreheadEyes from "@/assets/social-bg/forehead-eyes.png";
import blondePink from "@/assets/social-bg/blonde-pink.png";
import greenEyeliner from "@/assets/social-bg/green-eyeliner.png";
import lipsCloseup from "@/assets/social-bg/lips-closeup.png";
import curlyStrawberry from "@/assets/social-bg/curly-strawberry.png";
import curlyTortoise from "@/assets/social-bg/curly-tortoise.png";
import beachGlow from "@/assets/social-bg/beach-glow.png";
import goldenProfile from "@/assets/social-bg/golden-profile.png";
import neckNecklace from "@/assets/social-bg/neck-necklace.png";
import purpleEye from "@/assets/social-bg/purple-eye.png";
import bunForehead from "@/assets/social-bg/bun-forehead.png";
import orangeLiner from "@/assets/social-bg/orange-liner.png";
import tealEye from "@/assets/social-bg/teal-eye.png";
import closedEyesSun from "@/assets/social-bg/closed-eyes-sun.png";

// Import logo images
import truetoxToxPassCream from "@/assets/logos/truetox-tox-pass-cream.png";
import truetoxWhite from "@/assets/logos/truetox-white.png";
import truetoxToxPassOrange from "@/assets/logos/truetox-tox-pass-orange.png";
import truetoxOrange from "@/assets/logos/truetox-orange.png";
import truetoxToxPassBlack from "@/assets/logos/truetox-tox-pass-black.png";
import providerBadgeOrange from "@/assets/logos/provider-badge-orange.png";

// Background image options
const SAMPLE_BACKGROUNDS = [
  { id: "smile-closeup", name: "Smile Closeup", url: smileCloseup },
  { id: "stretch-pose", name: "Stretch Pose", url: stretchPose },
  { id: "jawline", name: "Jawline", url: jawline },
  { id: "lips-sunglasses", name: "Lips & Sunglasses", url: lipsSunglasses },
  { id: "forehead-eyes", name: "Forehead & Eyes", url: foreheadEyes },
  { id: "blonde-pink", name: "Blonde Pink", url: blondePink },
  { id: "green-eyeliner", name: "Green Eyeliner", url: greenEyeliner },
  { id: "lips-closeup", name: "Lips Closeup", url: lipsCloseup },
  { id: "curly-strawberry", name: "Curly Strawberry", url: curlyStrawberry },
  { id: "curly-tortoise", name: "Curly Tortoise", url: curlyTortoise },
  { id: "beach-glow", name: "Beach Glow", url: beachGlow },
  { id: "golden-profile", name: "Golden Profile", url: goldenProfile },
  { id: "neck-necklace", name: "Neck & Necklace", url: neckNecklace },
  { id: "purple-eye", name: "Purple Eye", url: purpleEye },
  { id: "bun-forehead", name: "Bun Forehead", url: bunForehead },
  { id: "orange-liner", name: "Orange Liner", url: orangeLiner },
  { id: "teal-eye", name: "Teal Eye", url: tealEye },
  { id: "closed-eyes-sun", name: "Closed Eyes Sun", url: closedEyesSun },
];

// Video background options
const VIDEO_BACKGROUNDS = [
  { id: "abstract-motion", name: "Abstract Motion", url: "/social-videos/abstract-motion.mp4" },
  { id: "grok-beauty", name: "Beauty Glow", url: "/social-videos/grok-beauty.mp4" },
  { id: "medical-spa", name: "Medical Spa", url: "/social-videos/medical-spa.mp4" },
  { id: "wellness-spa", name: "Wellness Spa", url: "/social-videos/wellness-spa.mp4" },
  { id: "beauty-treatment", name: "Beauty Treatment", url: "/social-videos/beauty-treatment.mp4" },
  { id: "aesthetic-clinic", name: "Aesthetic Clinic", url: "/social-videos/aesthetic-clinic.mp4" },
];

// Updated color palette per user request
const COLOR_PALETTE = [
  { id: "gold", name: "Gold", value: "#ffdb4d" },
  { id: "orange", name: "Orange", value: "#e26116" },
  { id: "charcoal", name: "Charcoal", value: "#181818" },
  { id: "cream", name: "Cream", value: "#eaeae0" },
  { id: "taupe", name: "Taupe", value: "#b79b7b" },
  { id: "beige", name: "Beige", value: "#e6dcb9" },
];

// Animation style options
const ANIMATION_STYLES = [
  { id: "fade-up", name: "Fade Up", textClass: "animate-fade-in", bgClass: "" },
  { id: "slide-in", name: "Slide In", textClass: "animate-slide-in-right", bgClass: "" },
  { id: "scale-pop", name: "Scale Pop", textClass: "animate-scale-in", bgClass: "" },
  { id: "ken-burns", name: "Ken Burns", textClass: "animate-fade-in", bgClass: "animate-ken-burns" },
];

// Music options for animations
const MUSIC_OPTIONS = [
  { id: "none", name: "No Music", url: null },
  { id: "chill-beats", name: "Chill Beats", url: "/social-videos/chill-beats.mp3" },
  { id: "waltz", name: "Waltz", url: "/social-videos/waltz.mp3" },
  { id: "country", name: "Country", url: "/social-videos/country.mp3" },
  { id: "upbeat", name: "Upbeat", url: "/social-videos/upbeat.mp3" },
  { id: "reggae", name: "Reggae", url: "/social-videos/reggae.mp3" },
  { id: "rock", name: "Rock", url: "/social-videos/rock.mp3" },
  { id: "latin", name: "Latin", url: "/social-videos/latin.mp3" },
  { id: "trap", name: "Trap", url: "/social-videos/trap.mp3" },
  { id: "freak", name: "Freak", url: "/social-videos/freak.mp3" },
];

// Logo preset options
const LOGO_PRESETS = [
  { id: "truetox-cream", name: "TrueTox + Tox Pass (Cream)", url: truetoxToxPassCream, bestFor: "Light backgrounds" },
  { id: "truetox-white", name: "TrueTox (White)", url: truetoxWhite, bestFor: "Dark backgrounds" },
  { id: "truetox-orange-full", name: "TrueTox + Tox Pass (Orange)", url: truetoxToxPassOrange, bestFor: "General use" },
  { id: "truetox-orange", name: "TrueTox (Orange)", url: truetoxOrange, bestFor: "Minimal branding" },
  { id: "truetox-black", name: "TrueTox + Tox Pass (Black)", url: truetoxToxPassBlack, bestFor: "Light backgrounds" },
  { id: "provider-badge", name: "Provider Badge", url: providerBadgeOrange, bestFor: "Trust/authority" },
];

// Video thumbnail component
const VideoThumbnail = ({ url, name }: { url: string; name: string }) => {
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  
  useEffect(() => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = url;
    video.muted = true;
    video.currentTime = 1;
    
    video.onloadeddata = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 40;
      canvas.height = 72;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        setThumbnail(canvas.toDataURL('image/jpeg', 0.5));
      }
    };
  }, [url]);
  
  return (
    <div className="flex items-center gap-2">
      {thumbnail ? (
        <img src={thumbnail} alt={name} className="w-6 h-10 object-cover rounded" />
      ) : (
        <div className="w-6 h-10 bg-muted rounded flex items-center justify-center">
          <Video className="w-3 h-3" />
        </div>
      )}
      <span>{name}</span>
    </div>
  );
};

interface ProviderBranding {
  clinicName?: string;
  clinicLogo?: string;
  providerHeadshot?: string;
}

interface SocialMediaAssetGeneratorProps {
  providerBranding?: ProviderBranding;
  isAdmin?: boolean;
}

export const SocialMediaAssetGenerator = ({ 
  providerBranding, 
  isAdmin = false 
}: SocialMediaAssetGeneratorProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tiktokFileInputRef = useRef<HTMLInputElement>(null);
  const storyVideoInputRef = useRef<HTMLInputElement>(null);
  const tiktokVideoInputRef = useRef<HTMLInputElement>(null);
  const storyLogoInputRef = useRef<HTMLInputElement>(null);
  const tiktokLogoInputRef = useRef<HTMLInputElement>(null);

  // Video preview state
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [videoPreviewType, setVideoPreviewType] = useState<'story' | 'tiktok'>('story');
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  
  // Thumbnail selection state
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(0);
  const [showThumbnailSelector, setShowThumbnailSelector] = useState(false);

  // Animation state
  const [animationEnabled, setAnimationEnabled] = useState(true);
  const [animationStyle, setAnimationStyle] = useState("fade-up");
  const [selectedMusic, setSelectedMusic] = useState("none");
  const [showSafetyZone, setShowSafetyZone] = useState(true);

  // Get current animation classes based on selected style
  const getAnimationClasses = () => {
    const style = ANIMATION_STYLES.find(s => s.id === animationStyle);
    return style || ANIMATION_STYLES[0];
  };

  // Instagram Story State
  const [storyHeadline, setStoryHeadline] = useState("Stop counting units 💉");
  const [storyBody, setStoryBody] = useState("$189/month = UNLIMITED full-face treatments");
  const [storySubtext, setStorySubtext] = useState("The math doesn't lie.");
  const [storyBackground, setStoryBackground] = useState(SAMPLE_BACKGROUNDS[0].url);
  const [storyCustomBackground, setStoryCustomBackground] = useState<string | null>(null);
  const [storyVideoBackground, setStoryVideoBackground] = useState<string | null>(null);
  const [storyVideoStart, setStoryVideoStart] = useState(0);
  const [storyVideoEnd, setStoryVideoEnd] = useState(8.5);
  const [storyCta, setStoryCta] = useState(providerBranding?.clinicName ? `Book at ${providerBranding.clinicName}` : "Find a True Provider");
  const [storyTextColor, setStoryTextColor] = useState("#eaeae0");
  const [storyCtaColor, setStoryCtaColor] = useState("#ffdb4d");
  const [storyIsReel, setStoryIsReel] = useState(false);

  // TikTok State
  const [tiktokHook, setTiktokHook] = useState("POV: You stopped counting units");
  const [tiktokSubline, setTiktokSubline] = useState("$189/mo flat-rate Botox");
  const [tiktokBackground, setTiktokBackground] = useState(SAMPLE_BACKGROUNDS[1].url);
  const [tiktokCustomBackground, setTiktokCustomBackground] = useState<string | null>(null);
  const [tiktokVideoBackground, setTiktokVideoBackground] = useState<string | null>(null);
  const [tiktokVideoStart, setTiktokVideoStart] = useState(0);
  const [tiktokVideoEnd, setTiktokVideoEnd] = useState(8.5);
  const [tiktokCta, setTiktokCta] = useState("Link in bio 👇");
  const [tiktokTextColor, setTiktokTextColor] = useState("#eaeae0");
  const [tiktokCtaColor, setTiktokCtaColor] = useState("#ffdb4d");
  const [tiktokShowCta, setTiktokShowCta] = useState(true);

  // Instagram Story Logo State
  const [storyLogoEnabled, setStoryLogoEnabled] = useState(false);
  const [storyLogo, setStoryLogo] = useState<string | null>(null);
  const [storyLogoPosition, setStoryLogoPosition] = useState({ x: 6, y: 15 });
  const [storyLogoSize, setStoryLogoSize] = useState(60);
  const [storyLogoOpacity, setStoryLogoOpacity] = useState(1);
  const [storyCustomLogo, setStoryCustomLogo] = useState<string | null>(null);

  // TikTok Logo State
  const [tiktokLogoEnabled, setTiktokLogoEnabled] = useState(false);
  const [tiktokLogo, setTiktokLogo] = useState<string | null>(null);
  const [tiktokLogoPosition, setTiktokLogoPosition] = useState({ x: 6, y: 12 });
  const [tiktokLogoSize, setTiktokLogoSize] = useState(60);
  const [tiktokLogoOpacity, setTiktokLogoOpacity] = useState(1);
  const [tiktokCustomLogo, setTiktokCustomLogo] = useState<string | null>(null);

  // Drag-and-drop state
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [dragTarget, setDragTarget] = useState<'story' | 'tiktok' | null>(null);
  const dragStartRef = useRef<{ x: number; y: number; logoX: number; logoY: number } | null>(null);

  // Animation classes for elements
  const [isAnimating, setIsAnimating] = useState(false);

  // Library state
  const [showLibrary, setShowLibrary] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveContentName, setSaveContentName] = useState("");
  const [saveContentType, setSaveContentType] = useState<'story' | 'tiktok'>('story');
  const [savedContent, setSavedContent] = useState<any[]>([]);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch saved content
  const fetchSavedContent = useCallback(async () => {
    setIsLoadingLibrary(true);
    try {
      const { data, error } = await supabase
        .from('saved_social_content')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setSavedContent(data || []);
    } catch (error) {
      console.error('Error fetching saved content:', error);
    } finally {
      setIsLoadingLibrary(false);
    }
  }, []);

  useEffect(() => {
    if (showLibrary) {
      fetchSavedContent();
    }
  }, [showLibrary, fetchSavedContent]);

  // Logo drag-and-drop handlers
  const handleLogoMouseDown = (e: React.MouseEvent, target: 'story' | 'tiktok') => {
    e.preventDefault();
    e.stopPropagation();
    
    const position = target === 'story' ? storyLogoPosition : tiktokLogoPosition;
    
    setIsDraggingLogo(true);
    setDragTarget(target);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      logoX: position.x,
      logoY: position.y,
    };
  };

  const handleLogoMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingLogo || !dragTarget || !dragStartRef.current) return;
    
    const canvasWidth = 270;
    const canvasHeight = 480;
    
    const deltaX = ((e.clientX - dragStartRef.current.x) / canvasWidth) * 100;
    const deltaY = ((e.clientY - dragStartRef.current.y) / canvasHeight) * 100;
    
    const newX = Math.max(0, Math.min(85, dragStartRef.current.logoX + deltaX));
    const newY = Math.max(0, Math.min(85, dragStartRef.current.logoY + deltaY));
    
    if (dragTarget === 'story') {
      setStoryLogoPosition({ x: newX, y: newY });
    } else {
      setTiktokLogoPosition({ x: newX, y: newY });
    }
  }, [isDraggingLogo, dragTarget]);

  const handleLogoMouseUp = useCallback(() => {
    setIsDraggingLogo(false);
    setDragTarget(null);
    dragStartRef.current = null;
  }, []);

  useEffect(() => {
    if (isDraggingLogo) {
      window.addEventListener('mousemove', handleLogoMouseMove);
      window.addEventListener('mouseup', handleLogoMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleLogoMouseMove);
      window.removeEventListener('mouseup', handleLogoMouseUp);
    };
  }, [isDraggingLogo, handleLogoMouseMove, handleLogoMouseUp]);

  const handleCustomLogoUpload = (e: React.ChangeEvent<HTMLInputElement>, target: 'story' | 'tiktok') => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        if (target === 'story') {
          setStoryCustomLogo(dataUrl);
          setStoryLogo(null);
        } else {
          setTiktokCustomLogo(dataUrl);
          setTiktokLogo(null);
        }
        toast.success("Custom logo uploaded!");
      };
      reader.readAsDataURL(file);
    }
  };

  // Get current configuration for saving
  const getCurrentConfig = (type: 'story' | 'tiktok') => {
    if (type === 'story') {
      return {
        headline: storyHeadline,
        body: storyBody,
        subtext: storySubtext,
        background: storyBackground,
        videoBackground: storyVideoBackground,
        videoStart: storyVideoStart,
        videoEnd: storyVideoEnd,
        cta: storyCta,
        textColor: storyTextColor,
        ctaColor: storyCtaColor,
        isReel: storyIsReel,
        logoEnabled: storyLogoEnabled,
        logo: storyLogo,
        customLogo: storyCustomLogo,
        logoPosition: storyLogoPosition,
        logoSize: storyLogoSize,
        logoOpacity: storyLogoOpacity,
      };
    } else {
      return {
        hook: tiktokHook,
        subline: tiktokSubline,
        background: tiktokBackground,
        videoBackground: tiktokVideoBackground,
        videoStart: tiktokVideoStart,
        videoEnd: tiktokVideoEnd,
        cta: tiktokCta,
        textColor: tiktokTextColor,
        ctaColor: tiktokCtaColor,
        showCta: tiktokShowCta,
        logoEnabled: tiktokLogoEnabled,
        logo: tiktokLogo,
        customLogo: tiktokCustomLogo,
        logoPosition: tiktokLogoPosition,
        logoSize: tiktokLogoSize,
        logoOpacity: tiktokLogoOpacity,
      };
    }
  };

  // Save content to library
  const handleSaveContent = async () => {
    if (!saveContentName.trim()) {
      toast.error("Please enter a name for this content");
      return;
    }

    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get location_id for current user
      let locationId = null;
      if (user) {
        const { data: providerAccess } = await supabase
          .from('provider_access')
          .select('location_id')
          .eq('user_id', user.id)
          .eq('is_primary', true)
          .maybeSingle();
        locationId = providerAccess?.location_id;
      }

      const { error } = await supabase
        .from('saved_social_content')
        .insert({
          user_id: user?.id,
          location_id: locationId,
          content_type: saveContentType,
          name: saveContentName.trim(),
          configuration: getCurrentConfig(saveContentType),
        });

      if (error) throw error;

      toast.success("Content saved to library!");
      setShowSaveDialog(false);
      setSaveContentName("");
      fetchSavedContent();
    } catch (error: any) {
      console.error('Error saving content:', error);
      toast.error(error.message || "Failed to save content");
    } finally {
      setIsSaving(false);
    }
  };

  // Load content from library
  const handleLoadContent = (item: any) => {
    const config = item.configuration;
    
    if (item.content_type === 'story') {
      setStoryHeadline(config.headline || "");
      setStoryBody(config.body || "");
      setStorySubtext(config.subtext || "");
      setStoryBackground(config.background || SAMPLE_BACKGROUNDS[0].url);
      setStoryVideoBackground(config.videoBackground || null);
      setStoryVideoStart(config.videoStart || 0);
      setStoryVideoEnd(config.videoEnd || 8.5);
      setStoryCta(config.cta || "");
      setStoryTextColor(config.textColor || "#eaeae0");
      setStoryCtaColor(config.ctaColor || "#ffdb4d");
      setStoryIsReel(config.isReel || false);
      // Logo settings
      setStoryLogoEnabled(config.logoEnabled || false);
      setStoryLogo(config.logo || null);
      setStoryCustomLogo(config.customLogo || null);
      setStoryLogoPosition(config.logoPosition || { x: 6, y: 15 });
      setStoryLogoSize(config.logoSize || 60);
      setStoryLogoOpacity(config.logoOpacity || 1);
    } else {
      setTiktokHook(config.hook || "");
      setTiktokSubline(config.subline || "");
      setTiktokBackground(config.background || SAMPLE_BACKGROUNDS[1].url);
      setTiktokVideoBackground(config.videoBackground || null);
      setTiktokVideoStart(config.videoStart || 0);
      setTiktokVideoEnd(config.videoEnd || 8.5);
      setTiktokCta(config.cta || "");
      setTiktokTextColor(config.textColor || "#eaeae0");
      setTiktokCtaColor(config.ctaColor || "#ffdb4d");
      setTiktokShowCta(config.showCta !== false);
      // Logo settings
      setTiktokLogoEnabled(config.logoEnabled || false);
      setTiktokLogo(config.logo || null);
      setTiktokCustomLogo(config.customLogo || null);
      setTiktokLogoPosition(config.logoPosition || { x: 6, y: 12 });
      setTiktokLogoSize(config.logoSize || 60);
      setTiktokLogoOpacity(config.logoOpacity || 1);
    }

    setShowLibrary(false);
    toast.success(`Loaded "${item.name}"`);
  };

  // Delete saved content
  const handleDeleteContent = async (id: string) => {
    try {
      const { error } = await supabase
        .from('saved_social_content')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success("Content deleted");
      fetchSavedContent();
    } catch (error: any) {
      console.error('Error deleting content:', error);
      toast.error("Failed to delete content");
    }
  };

  const handleCustomBackgroundUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'story' | 'tiktok') => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        if (type === 'story') {
          setStoryCustomBackground(result);
          setStoryBackground(result);
        } else {
          setTiktokCustomBackground(result);
          setTiktokBackground(result);
        }
        toast.success("Custom background uploaded!");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCustomVideoUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'story' | 'tiktok') => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        toast.error("Please upload a video file");
        return;
      }
      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error("Video must be less than 50MB");
        return;
      }
      const url = URL.createObjectURL(file);
      if (type === 'story') {
        setStoryVideoBackground(url);
      } else {
        setTiktokVideoBackground(url);
      }
      toast.success("Custom video uploaded!");
    }
  };

  const handleDownloadImage = async (type: 'story' | 'tiktok') => {
    setIsDownloading(true);
    const elementId = type === 'story' ? 'story-canvas' : 'tiktok-canvas';
    
    try {
      const element = document.getElementById(elementId);
      if (!element) throw new Error('Canvas not found');

      // Temporarily hide safety zone for download
      const safetyZones = element.querySelectorAll('.safety-zone-indicator');
      safetyZones.forEach(zone => (zone as HTMLElement).style.display = 'none');

      const html2canvas = (await import('html2canvas')).default;
      
      // Preview is 270x480, need scale of 4 to get 1080x1920
      const canvas = await html2canvas(element, {
        scale: 4,
        backgroundColor: null,
        useCORS: true,
        allowTaint: true,
      });

      // Restore safety zones
      safetyZones.forEach(zone => (zone as HTMLElement).style.display = '');

      const link = document.createElement('a');
      link.download = `${generateFilename(type, 'Image')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success(`${type === 'story' ? 'Instagram Story' : 'TikTok Cover'} downloaded! (${canvas.width}x${canvas.height})`);
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Failed to download. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadVideo = async (type: 'story' | 'tiktok', forPreview: boolean = false) => {
    setIsRecording(true);
    const elementId = type === 'story' ? 'story-canvas' : 'tiktok-canvas';
    
    try {
      // Check for WebCodecs support
      if (typeof VideoEncoder === 'undefined') {
        toast.error('Your browser does not support video encoding. Please use Chrome or Edge.');
        return;
      }

      const element = document.getElementById(elementId);
      if (!element) throw new Error('Canvas not found');

      // Temporarily hide safety zone for recording
      const safetyZones = element.querySelectorAll('.safety-zone-indicator');
      safetyZones.forEach(zone => (zone as HTMLElement).style.display = 'none');

      toast.info(forPreview ? "Creating preview..." : "Creating MP4 video... Please wait ~10 seconds");

      // Start animation
      setIsAnimating(true);

      const html2canvas = (await import('html2canvas')).default;
      const { Muxer, ArrayBufferTarget } = await import('mp4-muxer');
      
      const fps = 30;
      const durationMs = 8500; // 8.5 seconds
      const totalFrames = Math.floor((durationMs / 1000) * fps);
      const width = 1080;
      const height = 1920;
      
      // Fixed timescale for consistent playback - use 90000 (common video timescale)
      const timescale = 90000;
      const frameDurationTicks = Math.round(timescale / fps); // 3000 ticks per frame at 30fps

      // Collect thumbnails for selection (every 2 seconds = every 60 frames)
      const thumbnailFrames: string[] = [];

      // Frame duration in microseconds for explicit control (33333μs = 1/30th second at 30fps)
      const frameDurationMicros = Math.round(1_000_000 / fps);
      const videoDurationSeconds = durationMs / 1000;
      
      // Audio configuration
      const audioSampleRate = 48000;
      const audioChannels = 2;
      
      // Get selected music URL
      const musicOption = MUSIC_OPTIONS.find(m => m.id === selectedMusic);
      const musicUrl = musicOption?.url;
      const hasAudio = !!musicUrl;
      
      // Create MP4 muxer with explicit frameRate and optional audio
      const muxer = new Muxer({
        target: new ArrayBufferTarget(),
        video: {
          codec: 'avc',
          width,
          height,
          frameRate: fps,
        },
        ...(hasAudio && {
          audio: {
            codec: 'aac',
            numberOfChannels: audioChannels,
            sampleRate: audioSampleRate,
          },
        }),
        fastStart: 'in-memory',
        firstTimestampBehavior: 'offset',
      });

      // Track output frame count for correct timestamps (FIX #1)
      let videoFrameCount = 0;

      // Create video encoder with frame-count-based timestamps for correct playback speed
      const encoder = new VideoEncoder({
        output: (chunk, meta) => {
          // Extract raw data from the encoded chunk
          const data = new Uint8Array(chunk.byteLength);
          chunk.copyTo(data);
          
          // Calculate timestamp from our frame count, NOT chunk.timestamp
          // This ensures correct playback speed regardless of encoder behavior
          const timestamp = videoFrameCount * frameDurationMicros;
          
          muxer.addVideoChunkRaw(
            data,
            chunk.type === 'key' ? 'key' : 'delta',
            timestamp,              // Our calculated timestamp
            frameDurationMicros,    // Force exact frame duration: 33333μs for 30fps
            meta
          );
          
          videoFrameCount++;
        },
        error: (e) => console.error('Encoder error:', e),
      });

      encoder.configure({
        codec: 'avc1.640028',
        width,
        height,
        bitrate: 8_000_000,
        framerate: fps,
        latencyMode: 'quality', // Process frames more synchronously
      });

      // Create a canvas for rendering frames
      const renderCanvas = document.createElement('canvas');
      renderCanvas.width = width;
      renderCanvas.height = height;
      const ctx = renderCanvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');

      // Encode video frames
      for (let i = 0; i < totalFrames; i++) {
        const capturedCanvas = await html2canvas(element, {
          scale: 4,
          backgroundColor: null,
          useCORS: true,
          allowTaint: true,
        });
        
        // Draw to render canvas at target resolution
        ctx.drawImage(capturedCanvas, 0, 0, width, height);
        
        // Capture thumbnails at specific intervals (0s, 2s, 4s, 6s, 8s)
        if (i % 60 === 0 && thumbnailFrames.length < 5) {
          thumbnailFrames.push(renderCanvas.toDataURL('image/jpeg', 0.7));
        }
        
        // Create VideoFrame with consistent microsecond timing
        const frame = new VideoFrame(renderCanvas, {
          timestamp: i * frameDurationMicros,
          duration: frameDurationMicros,
        });
        
        encoder.encode(frame, { keyFrame: i % 30 === 0 });
        frame.close();
        
        // Small delay to prevent UI freeze
        if (i % 5 === 0) {
          await new Promise(resolve => setTimeout(resolve, 1));
        }
      }

      // Flush video encoder
      await encoder.flush();
      encoder.close();

      // Encode audio if music is selected (FIX #2)
      if (hasAudio && musicUrl) {
        try {
          console.log('Fetching audio from:', musicUrl);
          const audioResponse = await fetch(musicUrl);
          const audioArrayBuffer = await audioResponse.arrayBuffer();
          
          // Decode audio using Web Audio API
          const audioContext = new AudioContext({ sampleRate: audioSampleRate });
          const audioBuffer = await audioContext.decodeAudioData(audioArrayBuffer);
          
          console.log('Audio decoded:', audioBuffer.duration, 'seconds,', audioBuffer.numberOfChannels, 'channels');
          
          // Create audio encoder
          const audioEncoder = new AudioEncoder({
            output: (chunk, meta) => {
              muxer.addAudioChunk(chunk, meta);
            },
            error: (e) => console.error('Audio encoder error:', e),
          });

          audioEncoder.configure({
            codec: 'mp4a.40.2', // AAC-LC
            numberOfChannels: audioChannels,
            sampleRate: audioSampleRate,
            bitrate: 128000,
          });

          // Calculate how many samples we need for the video duration
          const totalSamples = Math.floor(audioSampleRate * videoDurationSeconds);
          const availableSamples = Math.min(totalSamples, audioBuffer.length);
          
          // Create interleaved audio data (AudioEncoder expects interleaved f32)
          const interleavedData = new Float32Array(availableSamples * audioChannels);
          
          for (let i = 0; i < availableSamples; i++) {
            for (let ch = 0; ch < audioChannels; ch++) {
              // Get channel data (use channel 0 for mono source, or actual channel for stereo)
              const sourceChannel = Math.min(ch, audioBuffer.numberOfChannels - 1);
              interleavedData[i * audioChannels + ch] = audioBuffer.getChannelData(sourceChannel)[i] || 0;
            }
          }

          // Encode audio in chunks (1024 samples per AAC frame is typical)
          const samplesPerChunk = 1024;
          let audioTimestamp = 0;
          
          for (let offset = 0; offset < availableSamples; offset += samplesPerChunk) {
            const chunkSamples = Math.min(samplesPerChunk, availableSamples - offset);
            const chunkData = new Float32Array(chunkSamples * audioChannels);
            
            for (let i = 0; i < chunkSamples * audioChannels; i++) {
              chunkData[i] = interleavedData[offset * audioChannels + i] || 0;
            }

            const audioData = new AudioData({
              format: 'f32-planar',
              sampleRate: audioSampleRate,
              numberOfFrames: chunkSamples,
              numberOfChannels: audioChannels,
              timestamp: audioTimestamp,
              data: chunkData,
            });

            audioEncoder.encode(audioData);
            audioData.close();
            
            audioTimestamp += Math.round((chunkSamples / audioSampleRate) * 1_000_000);
          }

          await audioEncoder.flush();
          audioEncoder.close();
          await audioContext.close();
          
          console.log('Audio encoding complete');
        } catch (audioError) {
          console.error('Audio encoding failed, video will be silent:', audioError);
          toast.warning('Audio encoding failed. Video will be silent.');
        }
      }

      // Finalize muxer
      muxer.finalize();

      // Restore safety zones and stop animation
      safetyZones.forEach(zone => (zone as HTMLElement).style.display = '');
      setIsAnimating(false);

      // Create blob and URL
      const { buffer } = muxer.target as InstanceType<typeof ArrayBufferTarget>;
      const blob = new Blob([buffer], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);
      
      // Set thumbnails for selection
      setThumbnails(thumbnailFrames);
      setSelectedThumbnailIndex(0);

      if (forPreview) {
        // Show preview dialog
        setVideoPreviewUrl(url);
        setVideoPreviewType(type);
        setShowVideoPreview(true);
      } else {
        // Direct download
        const link = document.createElement('a');
        link.download = `${generateFilename(type, 'Reel')}.mp4`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        toast.success(`${type === 'story' ? 'Instagram Story' : 'TikTok'} MP4 video downloaded!`);
      }

    } catch (error) {
      console.error('Error creating video:', error);
      toast.error('Failed to create video. Try downloading as image instead.');
      setIsAnimating(false);
    } finally {
      setIsRecording(false);
    }
  };

  const handleDownloadFromPreview = () => {
    if (videoPreviewUrl) {
      const link = document.createElement('a');
      link.download = `${generateFilename(videoPreviewType, 'Reel')}.mp4`;
      link.href = videoPreviewUrl;
      link.click();
      toast.success(`${videoPreviewType === 'story' ? 'Instagram Story' : 'TikTok'} MP4 video downloaded!`);
    }
  };

  const handleDownloadThumbnail = () => {
    if (thumbnails[selectedThumbnailIndex]) {
      const link = document.createElement('a');
      link.download = `${generateFilename(videoPreviewType, 'Thumb')}.jpg`;
      link.href = thumbnails[selectedThumbnailIndex];
      link.click();
      toast.success('Thumbnail downloaded!');
    }
  };

  const closeVideoPreview = () => {
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
    }
    setShowVideoPreview(false);
    setVideoPreviewUrl(null);
    setThumbnails([]);
  };

  // Get contrast color for text on button
  const getContrastColor = (hexColor: string): string => {
    const lightColors = ["#ffdb4d", "#eaeae0", "#e6dcb9"];
    return lightColors.includes(hexColor.toLowerCase()) ? "#181818" : "#eaeae0";
  };

  // Generate descriptive filename for downloads
  const generateFilename = (type: 'story' | 'tiktok', format: 'Image' | 'Reel' | 'Thumb') => {
    const headline = type === 'story' ? storyHeadline : tiktokHook;
    const isReel = type === 'story' && storyIsReel;
    
    // Clean headline: remove emojis, special chars, replace spaces with underscores
    const cleanHeadline = headline
      .replace(/[\u{1F600}-\u{1F6FF}]/gu, '') // Remove emojis
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special chars
      .trim()
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .substring(0, 50); // Limit length
    
    // Determine content type label
    let typeLabel: string;
    if (type === 'tiktok') {
      typeLabel = 'TikTok';
    } else if (isReel) {
      typeLabel = 'Reel';
    } else {
      typeLabel = 'Story';
    }
    
    // Format date as MMDDYYYY
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const year = now.getFullYear();
    const dateStr = `${month}${day}${year}`;
    
    // Add format suffix for video/image distinction
    const formatSuffix = format === 'Image' ? '_Image' : format === 'Thumb' ? '_Thumb' : '';
    
    return `${cleanHeadline}_${typeLabel}${formatSuffix}_${dateStr}`;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Social Media Asset Generator
            <Badge variant="secondary">Beta</Badge>
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowLibrary(true)}
            className="flex items-center gap-2"
          >
            <Library className="w-4 h-4" />
            Library
            {savedContent.length > 0 && (
              <Badge variant="secondary" className="ml-1">{savedContent.length}</Badge>
            )}
          </Button>
        </div>
        <CardDescription>
          Create branded social media graphics for Instagram Stories and TikTok. 
          {providerBranding?.clinicName && (
            <span className="text-primary font-medium"> Auto-customized for {providerBranding.clinicName}</span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Global Controls */}
        <div className="flex flex-wrap gap-4 mb-6 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Switch 
              checked={animationEnabled} 
              onCheckedChange={setAnimationEnabled}
              id="animation-toggle"
            />
            <Label htmlFor="animation-toggle" className="text-sm">Enable Animation</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch 
              checked={showSafetyZone} 
              onCheckedChange={setShowSafetyZone}
              id="safety-zone-toggle"
            />
            <Label htmlFor="safety-zone-toggle" className="text-sm flex items-center gap-1">
              {showSafetyZone ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              Safety Zone
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="animation-style" className="text-sm">Style:</Label>
            <Select value={animationStyle} onValueChange={setAnimationStyle} disabled={!animationEnabled}>
              <SelectTrigger className="w-[130px] h-8">
                <SelectValue placeholder="Select style" />
              </SelectTrigger>
              <SelectContent>
                {ANIMATION_STYLES.map((style) => (
                  <SelectItem key={style.id} value={style.id}>
                    {style.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="music-select" className="text-sm">Music:</Label>
            <Select value={selectedMusic} onValueChange={setSelectedMusic}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue placeholder="Select music" />
              </SelectTrigger>
              <SelectContent>
                {MUSIC_OPTIONS.map((music) => (
                  <SelectItem key={music.id} value={music.id}>
                    <div className="flex items-center gap-2">
                      <Music className="w-3 h-3" />
                      {music.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs defaultValue="instagram" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="instagram" className="flex items-center gap-2">
              <Instagram className="w-4 h-4" />
              Instagram Story
            </TabsTrigger>
            <TabsTrigger value="tiktok" className="flex items-center gap-2">
              <Video className="w-4 h-4" />
              TikTok
            </TabsTrigger>
          </TabsList>

          {/* Instagram Story Template */}
          <TabsContent value="instagram" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Preview */}
              <div className="space-y-4">
                <Label>Preview (9:16 ratio)</Label>
                <div 
                  id="story-canvas"
                  ref={canvasRef}
                  className={`relative rounded-lg overflow-hidden shadow-xl mx-auto ${isAnimating ? 'animate-pulse' : ''}`}
                  style={{ 
                    width: '270px', 
                    height: '480px',
                  }}
                >
                  {/* Video or Image Background */}
                  {storyVideoBackground ? (
                    <video
                      className={`absolute inset-0 w-full h-full object-cover ${animationEnabled && getAnimationClasses().bgClass ? getAnimationClasses().bgClass : ''}`}
                      src={storyVideoBackground}
                      autoPlay
                      loop
                      muted
                      playsInline
                      onLoadedMetadata={(e) => {
                        const video = e.currentTarget;
                        video.currentTime = storyVideoStart;
                      }}
                      onTimeUpdate={(e) => {
                        const video = e.currentTarget;
                        if (video.currentTime >= storyVideoEnd) {
                          video.currentTime = storyVideoStart;
                        }
                      }}
                    />
                  ) : (
                    <div 
                      className={`absolute inset-0 ${animationEnabled && getAnimationClasses().bgClass ? getAnimationClasses().bgClass : ''}`}
                      style={{ 
                        backgroundImage: `url(${storyCustomBackground || storyBackground})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        transformOrigin: 'center center',
                      }}
                    />
                  )}
                  {/* Safety zone indicator */}
                  {showSafetyZone && (
                    <div className="safety-zone-indicator absolute inset-0 pointer-events-none">
                      {/* Top safe zone (14% for camera notch/status bar) */}
                      <div className="absolute top-0 left-0 right-0 h-[14%] border-b-2 border-dashed border-yellow-400/60 bg-yellow-400/10" />
                      {/* Bottom safe zone (20% for swipe up/CTA area) */}
                      <div className="absolute bottom-0 left-0 right-0 h-[20%] border-t-2 border-dashed border-yellow-400/60 bg-yellow-400/10" />
                      {/* Side margins (5% each side) */}
                      <div className="absolute top-0 bottom-0 left-0 w-[5%] border-r-2 border-dashed border-yellow-400/60 bg-yellow-400/10" />
                      <div className="absolute top-0 bottom-0 right-0 w-[5%] border-l-2 border-dashed border-yellow-400/60 bg-yellow-400/10" />
                    </div>
                  )}

                  {/* Logo Overlay - Draggable */}
                  {storyLogoEnabled && (storyLogo || storyCustomLogo) && (
                    <img
                      src={storyCustomLogo || storyLogo || ''}
                      alt="Logo overlay"
                      className={`absolute select-none transition-transform duration-150 ${
                        isDraggingLogo && dragTarget === 'story' 
                          ? 'cursor-grabbing scale-105 ring-2 ring-primary' 
                          : 'cursor-grab hover:scale-105'
                      }`}
                      style={{
                        left: `${storyLogoPosition.x}%`,
                        top: `${storyLogoPosition.y}%`,
                        width: `${storyLogoSize}px`,
                        height: 'auto',
                        opacity: storyLogoOpacity,
                        zIndex: 20,
                        pointerEvents: 'auto',
                      }}
                      onMouseDown={(e) => handleLogoMouseDown(e, 'story')}
                      draggable={false}
                    />
                  )}

                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black/80" />
                  
                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-between px-[8%] pt-[18%] pb-[24%]" style={{ color: storyTextColor }}>
                    {/* Top - Headline */}
                    <div className="space-y-2">
                      {providerBranding?.clinicLogo && (
                        <img 
                          src={providerBranding.clinicLogo} 
                          alt="Clinic Logo" 
                          className="w-16 h-16 object-contain mb-4"
                        />
                      )}
                      <h2 
                        className={`text-[2.55rem] font-bold leading-none drop-shadow-lg ${animationEnabled ? getAnimationClasses().textClass : ''}`}
                        style={{ animationDelay: '0.2s' }}
                      >
                        {storyHeadline}
                      </h2>
                    </div>

                    {/* Middle - Body */}
                    <div className="space-y-3">
                      <p 
                        className={`text-xl font-semibold drop-shadow-md ${animationEnabled ? getAnimationClasses().textClass : ''}`}
                        style={{ animationDelay: '0.5s' }}
                      >
                        {storyBody}
                      </p>
                      <p 
                        className={`text-sm opacity-90 drop-shadow ${animationEnabled ? getAnimationClasses().textClass : ''}`}
                        style={{ animationDelay: '0.8s' }}
                      >
                        {storySubtext}
                      </p>
                    </div>

                    {/* Bottom - CTA (always rendered for consistent text positioning in Reel mode) */}
                    <div 
                      className={`space-y-3 ${animationEnabled ? getAnimationClasses().textClass : ''}`}
                      style={{ animationDelay: '1.1s' }}
                    >
                      {!storyIsReel && (
                        <div 
                          className="flex items-center justify-center px-4 py-2.5 rounded-full text-center font-bold text-sm shadow-lg"
                          style={{ backgroundColor: storyCtaColor, color: getContrastColor(storyCtaColor) }}
                        >
                          {storyCta}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleDownloadImage('story')} 
                      disabled={isDownloading || isRecording}
                      className="flex-1 flex items-center justify-center"
                    >
                      <ImageIcon className="w-4 h-4 mr-2" />
                      {isDownloading ? 'Generating...' : 'Download PNG'}
                    </Button>
                    <Button 
                      onClick={() => handleDownloadVideo('story', false)} 
                      disabled={isDownloading || isRecording}
                      variant="secondary"
                      className="flex-1 flex items-center justify-center"
                    >
                      <Video className="w-4 h-4 mr-2" />
                      {isRecording ? 'Recording...' : 'Download MP4'}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleDownloadVideo('story', true)} 
                      disabled={isDownloading || isRecording}
                      variant="outline"
                      className="flex-1 flex items-center justify-center"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {isRecording ? 'Creating Preview...' : 'Preview Video'}
                    </Button>
                    <Button 
                      onClick={() => {
                        setSaveContentType('story');
                        setShowSaveDialog(true);
                      }}
                      variant="outline"
                      className="flex items-center justify-center"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="space-y-4">
              <div className="space-y-2">
                  <Label htmlFor="story-headline">Headline</Label>
                  <Select onValueChange={(value) => setStoryHeadline(value)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a preset or type below..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A New Way to Tox">A New Way to Tox</SelectItem>
                      <SelectItem value="Full Face Correction. One Simple Membership.">Full Face Correction. One Simple Membership.</SelectItem>
                      <SelectItem value="All the Tox You Need. One Monthly Fee.">All the Tox You Need. One Monthly Fee.</SelectItem>
                      <SelectItem value="Stop Counting Units. Start Loving Your Results.">Stop Counting Units. Start Loving Your Results.</SelectItem>
                      <SelectItem value="Look Sharp. Stay Sharp. No Apologies.">Look Sharp. Stay Sharp. No Apologies.</SelectItem>
                      <SelectItem value="Tox Done Smarter — Not Harder.">Tox Done Smarter — Not Harder.</SelectItem>
                      <SelectItem value="The Tox Pass Has Arrived.">The Tox Pass Has Arrived.</SelectItem>
                      <SelectItem value="Unlimited Confidence. Predictable Pricing.">Unlimited Confidence. Predictable Pricing.</SelectItem>
                      <SelectItem value="The Future of Aesthetics has Arrived.">The Future of Aesthetics has Arrived.</SelectItem>
                      <SelectItem value="Subscribe to Smooth.">Subscribe to Smooth.</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input 
                    id="story-headline"
                    value={storyHeadline}
                    onChange={(e) => setStoryHeadline(e.target.value)}
                    placeholder="Or type a custom headline..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="story-body">Main Message</Label>
                  <Select onValueChange={(value) => setStoryBody(value)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a preset or type below..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A modern, membership-based approach that gives you full correction without surprise costs or confusing unit math.">Modern membership approach...</SelectItem>
                      <SelectItem value="Achieve balanced, natural-looking results with predictable monthly pricing and a treatment plan built for your unique goals.">Balanced, natural results...</SelectItem>
                      <SelectItem value="Never worry about 'how many units' again — your membership covers exactly what you need for ideal results every time.">Never worry about units...</SelectItem>
                      <SelectItem value="We focus on outcomes, not measurements. No overthinking. No upsells. Just confidence that lasts.">Focus on outcomes...</SelectItem>
                      <SelectItem value="Consistent, refreshed, full-face rejuvenation — for women who don't wait to 'age gracefully.'">Full-face rejuvenation...</SelectItem>
                      <SelectItem value="Powered by AI facial analysis + expert injectors to give you a personalized treatment roadmap.">AI facial analysis...</SelectItem>
                      <SelectItem value="The first membership that unlocks all the tox you need, member perks, and progress rewards at every visit.">First membership unlocks all...</SelectItem>
                      <SelectItem value="Keep your look polished year-round with monthly visits that fit your lifestyle and your budget.">Year-round polished look...</SelectItem>
                      <SelectItem value="Subscription aesthetics delivers consistency, better results, and a long-term anti-aging strategy.">Subscription aesthetics...</SelectItem>
                      <SelectItem value="Fast visits, no surprises, and a treatment plan that adapts to your life — not the other way around.">Fast visits, no surprises...</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input 
                    id="story-body"
                    value={storyBody}
                    onChange={(e) => setStoryBody(e.target.value)}
                    placeholder="Or type a custom message..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="story-subtext">Subtext</Label>
                  <Input 
                    id="story-subtext"
                    value={storySubtext}
                    onChange={(e) => setStorySubtext(e.target.value)}
                    placeholder="The math doesn't lie."
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="story-cta">Call to Action</Label>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={storyIsReel} 
                        onCheckedChange={setStoryIsReel}
                        id="story-reel-toggle"
                      />
                      <Label htmlFor="story-reel-toggle" className="text-xs text-muted-foreground">Make it a Reel?</Label>
                    </div>
                  </div>
                  <Select onValueChange={(value) => setStoryCta(value)} disabled={storyIsReel}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a preset or type below..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Get Your Tox Pass">Get Your Tox Pass</SelectItem>
                      <SelectItem value="Start Your Personalized Plan">Start Your Personalized Plan</SelectItem>
                      <SelectItem value="Ditch Units. Join TrueTox">Ditch Units. Join TrueTox</SelectItem>
                      <SelectItem value="See Your Best Results Yet">See Your Best Results Yet</SelectItem>
                      <SelectItem value="Refresh Your Look Today">Refresh Your Look Today</SelectItem>
                      <SelectItem value="Scan Your Face → See Your Plan">Scan Your Face → See Your Plan</SelectItem>
                      <SelectItem value="Unlock Member Access">Unlock Member Access</SelectItem>
                      <SelectItem value="Stay Consistent, Start Now">Stay Consistent, Start Now</SelectItem>
                      <SelectItem value="Join Subscription Aesthetics">Join Subscription Aesthetics</SelectItem>
                      <SelectItem value="Book Your TrueTox Visit">Book Your TrueTox Visit</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input 
                    id="story-cta"
                    value={storyCta}
                    onChange={(e) => setStoryCta(e.target.value)}
                    placeholder="Or type a custom CTA..."
                    disabled={storyIsReel}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Text Color</Label>
                  <Select value={storyTextColor} onValueChange={setStoryTextColor}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select color" />
                    </SelectTrigger>
                    <SelectContent>
                      {COLOR_PALETTE.map((color) => (
                        <SelectItem key={color.id} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full border border-border" 
                              style={{ backgroundColor: color.value }}
                            />
                            {color.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>CTA Button Color</Label>
                  <Select value={storyCtaColor} onValueChange={setStoryCtaColor}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select color" />
                    </SelectTrigger>
                    <SelectContent>
                      {COLOR_PALETTE.map((color) => (
                        <SelectItem key={color.id} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full border border-border" 
                              style={{ backgroundColor: color.value }}
                            />
                            {color.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Background Image</Label>
                  <div className="flex gap-2">
                    <Select value={storyCustomBackground ? 'custom' : storyBackground} onValueChange={(val) => {
                      if (val !== 'custom') {
                        setStoryBackground(val);
                        setStoryCustomBackground(null);
                        setStoryVideoBackground(null);
                      }
                    }}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select background" />
                      </SelectTrigger>
                      <SelectContent>
                        {storyCustomBackground && (
                          <SelectItem value="custom">Custom Upload</SelectItem>
                        )}
                        {SAMPLE_BACKGROUNDS.map((bg) => (
                          <SelectItem key={bg.id} value={bg.url}>
                            {bg.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4" />
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleCustomBackgroundUpload(e, 'story')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Video Background</Label>
                  <div className="flex gap-2">
                    <Select value={storyVideoBackground?.startsWith('blob:') ? 'custom' : (storyVideoBackground || 'none')} onValueChange={(val) => {
                      if (val === 'none') {
                        setStoryVideoBackground(null);
                      } else if (val !== 'custom') {
                        setStoryVideoBackground(val);
                      }
                    }}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select video background" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Video</SelectItem>
                        {storyVideoBackground?.startsWith('blob:') && (
                          <SelectItem value="custom">
                            <div className="flex items-center gap-2">
                              <Video className="w-3 h-3" />
                              Custom Upload
                            </div>
                          </SelectItem>
                        )}
                        {VIDEO_BACKGROUNDS.map((vid) => (
                          <SelectItem key={vid.id} value={vid.url}>
                            <VideoThumbnail url={vid.url} name={vid.name} />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => storyVideoInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4" />
                    </Button>
                    <input
                      ref={storyVideoInputRef}
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => handleCustomVideoUpload(e, 'story')}
                    />
                  </div>
                </div>

                {storyVideoBackground && (
                  <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Scissors className="w-4 h-4 text-muted-foreground" />
                      <Label className="text-sm font-medium">Video Trim</Label>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Start: {storyVideoStart.toFixed(1)}s</span>
                        <span>End: {storyVideoEnd.toFixed(1)}s</span>
                        <span>Duration: {(storyVideoEnd - storyVideoStart).toFixed(1)}s</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-10">Start</span>
                          <Slider
                            value={[storyVideoStart]}
                            onValueChange={(val) => setStoryVideoStart(Math.min(val[0], storyVideoEnd - 0.5))}
                            max={30}
                            step={0.1}
                            className="flex-1"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-10">End</span>
                          <Slider
                            value={[storyVideoEnd]}
                            onValueChange={(val) => setStoryVideoEnd(Math.max(val[0], storyVideoStart + 0.5))}
                            max={30}
                            step={0.1}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Logo Overlay Controls */}
                <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      Logo Overlay
                    </Label>
                    <Switch
                      checked={storyLogoEnabled}
                      onCheckedChange={setStoryLogoEnabled}
                    />
                  </div>
                  
                  {storyLogoEnabled && (
                    <div className="space-y-3">
                      <Select 
                        value={storyLogo || ''} 
                        onValueChange={(val) => {
                          setStoryLogo(val);
                          setStoryCustomLogo(null);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a logo" />
                        </SelectTrigger>
                        <SelectContent>
                          {LOGO_PRESETS.map((logo) => (
                            <SelectItem key={logo.id} value={logo.url}>
                              <div className="flex items-center gap-2">
                                <img src={logo.url} alt={logo.name} className="w-8 h-8 object-contain" />
                                <div className="flex flex-col">
                                  <span className="text-sm">{logo.name}</span>
                                  <span className="text-xs text-muted-foreground">{logo.bestFor}</span>
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <input
                        ref={storyLogoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleCustomLogoUpload(e, 'story')}
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => storyLogoInputRef.current?.click()}
                        className="w-full"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Custom Logo
                      </Button>
                      
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span>Size</span>
                          <span>{storyLogoSize}px</span>
                        </div>
                        <Slider
                          value={[storyLogoSize]}
                          onValueChange={(val) => setStoryLogoSize(val[0])}
                          min={30}
                          max={120}
                          step={5}
                        />
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span>Opacity</span>
                          <span>{Math.round(storyLogoOpacity * 100)}%</span>
                        </div>
                        <Slider
                          value={[storyLogoOpacity]}
                          onValueChange={(val) => setStoryLogoOpacity(val[0])}
                          min={0.3}
                          max={1}
                          step={0.05}
                        />
                      </div>
                      
                      <div className="text-xs text-muted-foreground text-center">
                        Position: {storyLogoPosition.x.toFixed(0)}%, {storyLogoPosition.y.toFixed(0)}%
                        <br />
                        <span className="text-primary font-medium">Drag logo on preview to reposition</span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-1">
                        <Button variant="outline" size="sm" className="text-xs h-7"
                          onClick={() => setStoryLogoPosition({ x: 6, y: 15 })}>
                          Top Left
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs h-7"
                          onClick={() => setStoryLogoPosition({ x: 42, y: 15 })}>
                          Top Center
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs h-7"
                          onClick={() => setStoryLogoPosition({ x: 78, y: 15 })}>
                          Top Right
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs h-7"
                          onClick={() => setStoryLogoPosition({ x: 6, y: 80 })}>
                          Bottom Left
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs h-7"
                          onClick={() => setStoryLogoPosition({ x: 42, y: 45 })}>
                          Center
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs h-7"
                          onClick={() => setStoryLogoPosition({ x: 78, y: 80 })}>
                          Bottom Right
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setStoryHeadline("Stop counting units 💉");
                    setStoryBody("$189/month = UNLIMITED full-face treatments");
                    setStorySubtext("The math doesn't lie.");
                    setStoryCta(providerBranding?.clinicName ? `Book at ${providerBranding.clinicName}` : "Find a True Provider");
                    setStoryTextColor("#eaeae0");
                    setStoryCtaColor("#ffdb4d");
                    setStoryIsReel(false);
                    setStoryCustomBackground(null);
                    setStoryVideoBackground(null);
                    setStoryBackground(SAMPLE_BACKGROUNDS[0].url);
                    setStoryLogoEnabled(false);
                    setStoryLogo(null);
                    setStoryCustomLogo(null);
                    setStoryLogoPosition({ x: 6, y: 15 });
                    setStoryLogoSize(60);
                    setStoryLogoOpacity(1);
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset to Default
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* TikTok Template */}
          <TabsContent value="tiktok" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Preview */}
              <div className="space-y-4">
                <Label>Preview (9:16 ratio)</Label>
                <div 
                  id="tiktok-canvas"
                  className={`relative rounded-lg overflow-hidden shadow-xl mx-auto ${isAnimating ? 'animate-pulse' : ''}`}
                  style={{ 
                    width: '270px', 
                    height: '480px',
                  }}
                >
                  {/* Video or Image Background */}
                  {tiktokVideoBackground ? (
                    <video
                      className={`absolute inset-0 w-full h-full object-cover ${animationEnabled && getAnimationClasses().bgClass ? getAnimationClasses().bgClass : ''}`}
                      src={tiktokVideoBackground}
                      autoPlay
                      loop
                      muted
                      playsInline
                      onLoadedMetadata={(e) => {
                        const video = e.currentTarget;
                        video.currentTime = tiktokVideoStart;
                      }}
                      onTimeUpdate={(e) => {
                        const video = e.currentTarget;
                        if (video.currentTime >= tiktokVideoEnd) {
                          video.currentTime = tiktokVideoStart;
                        }
                      }}
                    />
                  ) : (
                    <div 
                      className={`absolute inset-0 ${animationEnabled && getAnimationClasses().bgClass ? getAnimationClasses().bgClass : ''}`}
                      style={{ 
                        backgroundImage: `url(${tiktokCustomBackground || tiktokBackground})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        transformOrigin: 'center center',
                      }}
                    />
                  )}
                  {/* Safety zone indicator for TikTok */}
                  {showSafetyZone && (
                    <div className="safety-zone-indicator absolute inset-0 pointer-events-none">
                      {/* Top safe zone (10% for status bar) */}
                      <div className="absolute top-0 left-0 right-0 h-[10%] border-b-2 border-dashed border-cyan-400/60 bg-cyan-400/10" />
                      {/* Bottom safe zone (35% for TikTok UI elements) */}
                      <div className="absolute bottom-0 left-0 right-0 h-[35%] border-t-2 border-dashed border-cyan-400/60 bg-cyan-400/10" />
                      {/* Right side (15% for action buttons) */}
                      <div className="absolute top-0 bottom-0 right-0 w-[15%] border-l-2 border-dashed border-cyan-400/60 bg-cyan-400/10" />
                    </div>
                  )}

                  {/* Logo Overlay - Draggable */}
                  {tiktokLogoEnabled && (tiktokLogo || tiktokCustomLogo) && (
                    <img
                      src={tiktokCustomLogo || tiktokLogo || ''}
                      alt="Logo overlay"
                      className={`absolute select-none transition-transform duration-150 ${
                        isDraggingLogo && dragTarget === 'tiktok' 
                          ? 'cursor-grabbing scale-105 ring-2 ring-primary' 
                          : 'cursor-grab hover:scale-105'
                      }`}
                      style={{
                        left: `${tiktokLogoPosition.x}%`,
                        top: `${tiktokLogoPosition.y}%`,
                        width: `${tiktokLogoSize}px`,
                        height: 'auto',
                        opacity: tiktokLogoOpacity,
                        zIndex: 20,
                        pointerEvents: 'auto',
                      }}
                      onMouseDown={(e) => handleLogoMouseDown(e, 'tiktok')}
                      draggable={false}
                    />
                  )}

                  {/* Gradient overlay - more dramatic for TikTok */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/90" />
                  
                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-between px-[6%] pr-[18%] pt-[12%] pb-[38%]" style={{ color: tiktokTextColor }}>
                    {/* Top - Hook */}
                    <div>
                      <h2 
                        className={`text-[2.55rem] font-black leading-none drop-shadow-lg ${animationEnabled ? getAnimationClasses().textClass : ''}`}
                        style={{ animationDelay: '0.2s' }}
                      >
                        {tiktokHook}
                      </h2>
                    </div>

                    {/* Middle - Visual space for video thumbnail */}
                    <div className="flex-1 flex items-center justify-center">
                      {providerBranding?.providerHeadshot && (
                        <img 
                          src={providerBranding.providerHeadshot} 
                          alt="Provider" 
                          className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                        />
                      )}
                    </div>

                    {/* Bottom - Subline + CTA (positioned above the 35% bottom safe zone) */}
                    <div className="space-y-2">
                      <p 
                        className={`text-base font-bold drop-shadow-md ${animationEnabled ? getAnimationClasses().textClass : ''}`}
                        style={{ animationDelay: '0.5s' }}
                      >
                        {tiktokSubline}
                      </p>
                      {tiktokShowCta && (
                        <div
                          className={`inline-flex items-center justify-center px-3 py-2 rounded-full text-sm font-bold shadow-lg ${animationEnabled ? getAnimationClasses().textClass : ''}`}
                          style={{ 
                            backgroundColor: tiktokCtaColor, 
                            color: getContrastColor(tiktokCtaColor),
                            animationDelay: '0.8s' 
                          }}
                        >
                          {tiktokCta}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleDownloadImage('tiktok')} 
                      disabled={isDownloading || isRecording}
                      className="flex-1 flex items-center justify-center"
                    >
                      <ImageIcon className="w-4 h-4 mr-2" />
                      {isDownloading ? 'Generating...' : 'Download PNG'}
                    </Button>
                    <Button 
                      onClick={() => handleDownloadVideo('tiktok', false)} 
                      disabled={isDownloading || isRecording}
                      variant="secondary"
                      className="flex-1 flex items-center justify-center"
                    >
                      <Video className="w-4 h-4 mr-2" />
                      {isRecording ? 'Recording...' : 'Download MP4'}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleDownloadVideo('tiktok', true)} 
                      disabled={isDownloading || isRecording}
                      variant="outline"
                      className="flex-1 flex items-center justify-center"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {isRecording ? 'Creating Preview...' : 'Preview Video'}
                    </Button>
                    <Button 
                      onClick={() => {
                        setSaveContentType('tiktok');
                        setShowSaveDialog(true);
                      }}
                      variant="outline"
                      className="flex items-center justify-center"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tiktok-hook">Hook Text (Headline)</Label>
                  <Select onValueChange={(value) => setTiktokHook(value)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a preset or type below..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A New Way to Tox">A New Way to Tox</SelectItem>
                      <SelectItem value="Full Face Correction. One Simple Membership.">Full Face Correction. One Simple Membership.</SelectItem>
                      <SelectItem value="All the Tox You Need. One Monthly Fee.">All the Tox You Need. One Monthly Fee.</SelectItem>
                      <SelectItem value="Stop Counting Units. Start Loving Your Results.">Stop Counting Units. Start Loving Your Results.</SelectItem>
                      <SelectItem value="Look Sharp. Stay Sharp. No Apologies.">Look Sharp. Stay Sharp. No Apologies.</SelectItem>
                      <SelectItem value="Tox Done Smarter — Not Harder.">Tox Done Smarter — Not Harder.</SelectItem>
                      <SelectItem value="The Tox Pass Has Arrived.">The Tox Pass Has Arrived.</SelectItem>
                      <SelectItem value="Unlimited Confidence. Predictable Pricing.">Unlimited Confidence. Predictable Pricing.</SelectItem>
                      <SelectItem value="The Future of Aesthetics has Arrived.">The Future of Aesthetics has Arrived.</SelectItem>
                      <SelectItem value="Subscribe to Smooth.">Subscribe to Smooth.</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input 
                    id="tiktok-hook"
                    value={tiktokHook}
                    onChange={(e) => setTiktokHook(e.target.value)}
                    placeholder="Or type a custom hook..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tiktok-subline">Subline (Main Message)</Label>
                  <Select onValueChange={(value) => setTiktokSubline(value)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a preset or type below..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A modern, membership-based approach that gives you full correction without surprise costs or confusing unit math.">Modern membership approach...</SelectItem>
                      <SelectItem value="Achieve balanced, natural-looking results with predictable monthly pricing and a treatment plan built for your unique goals.">Balanced, natural results...</SelectItem>
                      <SelectItem value="Never worry about 'how many units' again — your membership covers exactly what you need for ideal results every time.">Never worry about units...</SelectItem>
                      <SelectItem value="We focus on outcomes, not measurements. No overthinking. No upsells. Just confidence that lasts.">Focus on outcomes...</SelectItem>
                      <SelectItem value="Consistent, refreshed, full-face rejuvenation — for women who don't wait to 'age gracefully.'">Full-face rejuvenation...</SelectItem>
                      <SelectItem value="Powered by AI facial analysis + expert injectors to give you a personalized treatment roadmap.">AI facial analysis...</SelectItem>
                      <SelectItem value="The first membership that unlocks all the tox you need, member perks, and progress rewards at every visit.">First membership unlocks all...</SelectItem>
                      <SelectItem value="Keep your look polished year-round with monthly visits that fit your lifestyle and your budget.">Year-round polished look...</SelectItem>
                      <SelectItem value="Subscription aesthetics delivers consistency, better results, and a long-term anti-aging strategy.">Subscription aesthetics...</SelectItem>
                      <SelectItem value="Fast visits, no surprises, and a treatment plan that adapts to your life — not the other way around.">Fast visits, no surprises...</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input 
                    id="tiktok-subline"
                    value={tiktokSubline}
                    onChange={(e) => setTiktokSubline(e.target.value)}
                    placeholder="Or type a custom subline..."
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="tiktok-cta">Call to Action</Label>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={tiktokShowCta} 
                        onCheckedChange={setTiktokShowCta}
                        id="tiktok-cta-toggle"
                      />
                      <Label htmlFor="tiktok-cta-toggle" className="text-xs text-muted-foreground">Show CTA</Label>
                    </div>
                  </div>
                  <Select onValueChange={(value) => setTiktokCta(value)} disabled={!tiktokShowCta}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a preset or type below..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Get Your Tox Pass">Get Your Tox Pass</SelectItem>
                      <SelectItem value="Start Your Personalized Plan">Start Your Personalized Plan</SelectItem>
                      <SelectItem value="Ditch Units. Join TrueTox">Ditch Units. Join TrueTox</SelectItem>
                      <SelectItem value="See Your Best Results Yet">See Your Best Results Yet</SelectItem>
                      <SelectItem value="Refresh Your Look Today">Refresh Your Look Today</SelectItem>
                      <SelectItem value="Scan Your Face → See Your Plan">Scan Your Face → See Your Plan</SelectItem>
                      <SelectItem value="Unlock Member Access">Unlock Member Access</SelectItem>
                      <SelectItem value="Stay Consistent, Start Now">Stay Consistent, Start Now</SelectItem>
                      <SelectItem value="Join Subscription Aesthetics">Join Subscription Aesthetics</SelectItem>
                      <SelectItem value="Book Your TrueTox Visit">Book Your TrueTox Visit</SelectItem>
                      <SelectItem value="Link in bio 👇">Link in bio 👇</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input 
                    id="tiktok-cta"
                    value={tiktokCta}
                    onChange={(e) => setTiktokCta(e.target.value)}
                    placeholder="Or type a custom CTA..."
                    disabled={!tiktokShowCta}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Text Color</Label>
                  <Select value={tiktokTextColor} onValueChange={setTiktokTextColor}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select color" />
                    </SelectTrigger>
                    <SelectContent>
                      {COLOR_PALETTE.map((color) => (
                        <SelectItem key={color.id} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full border border-border" 
                              style={{ backgroundColor: color.value }}
                            />
                            {color.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>CTA Button Color</Label>
                  <Select value={tiktokCtaColor} onValueChange={setTiktokCtaColor}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select color" />
                    </SelectTrigger>
                    <SelectContent>
                      {COLOR_PALETTE.map((color) => (
                        <SelectItem key={color.id} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-4 h-4 rounded-full border border-border" 
                              style={{ backgroundColor: color.value }}
                            />
                            {color.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Background Image</Label>
                  <div className="flex gap-2">
                    <Select value={tiktokCustomBackground ? 'custom' : tiktokBackground} onValueChange={(val) => {
                      if (val !== 'custom') {
                        setTiktokBackground(val);
                        setTiktokCustomBackground(null);
                        setTiktokVideoBackground(null);
                      }
                    }}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select background" />
                      </SelectTrigger>
                      <SelectContent>
                        {tiktokCustomBackground && (
                          <SelectItem value="custom">Custom Upload</SelectItem>
                        )}
                        {SAMPLE_BACKGROUNDS.map((bg) => (
                          <SelectItem key={bg.id} value={bg.url}>
                            {bg.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => tiktokFileInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4" />
                    </Button>
                    <input
                      ref={tiktokFileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleCustomBackgroundUpload(e, 'tiktok')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Video Background</Label>
                  <div className="flex gap-2">
                    <Select value={tiktokVideoBackground?.startsWith('blob:') ? 'custom' : (tiktokVideoBackground || 'none')} onValueChange={(val) => {
                      if (val === 'none') {
                        setTiktokVideoBackground(null);
                      } else if (val !== 'custom') {
                        setTiktokVideoBackground(val);
                      }
                    }}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select video background" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Video</SelectItem>
                        {tiktokVideoBackground?.startsWith('blob:') && (
                          <SelectItem value="custom">
                            <div className="flex items-center gap-2">
                              <Video className="w-3 h-3" />
                              Custom Upload
                            </div>
                          </SelectItem>
                        )}
                        {VIDEO_BACKGROUNDS.map((vid) => (
                          <SelectItem key={vid.id} value={vid.url}>
                            <VideoThumbnail url={vid.url} name={vid.name} />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => tiktokVideoInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4" />
                    </Button>
                    <input
                      ref={tiktokVideoInputRef}
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => handleCustomVideoUpload(e, 'tiktok')}
                    />
                  </div>
                </div>

                {tiktokVideoBackground && (
                  <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Scissors className="w-4 h-4 text-muted-foreground" />
                      <Label className="text-sm font-medium">Video Trim</Label>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Start: {tiktokVideoStart.toFixed(1)}s</span>
                        <span>End: {tiktokVideoEnd.toFixed(1)}s</span>
                        <span>Duration: {(tiktokVideoEnd - tiktokVideoStart).toFixed(1)}s</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-10">Start</span>
                          <Slider
                            value={[tiktokVideoStart]}
                            onValueChange={(val) => setTiktokVideoStart(Math.min(val[0], tiktokVideoEnd - 0.5))}
                            max={30}
                            step={0.1}
                            className="flex-1"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-10">End</span>
                          <Slider
                            value={[tiktokVideoEnd]}
                            onValueChange={(val) => setTiktokVideoEnd(Math.max(val[0], tiktokVideoStart + 0.5))}
                            max={30}
                            step={0.1}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Logo Overlay Controls */}
                <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      Logo Overlay
                    </Label>
                    <Switch
                      checked={tiktokLogoEnabled}
                      onCheckedChange={setTiktokLogoEnabled}
                    />
                  </div>
                  
                  {tiktokLogoEnabled && (
                    <div className="space-y-3">
                      <Select 
                        value={tiktokLogo || ''} 
                        onValueChange={(val) => {
                          setTiktokLogo(val);
                          setTiktokCustomLogo(null);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a logo" />
                        </SelectTrigger>
                        <SelectContent>
                          {LOGO_PRESETS.map((logo) => (
                            <SelectItem key={logo.id} value={logo.url}>
                              <div className="flex items-center gap-2">
                                <img src={logo.url} alt={logo.name} className="w-8 h-8 object-contain" />
                                <div className="flex flex-col">
                                  <span className="text-sm">{logo.name}</span>
                                  <span className="text-xs text-muted-foreground">{logo.bestFor}</span>
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <input
                        ref={tiktokLogoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleCustomLogoUpload(e, 'tiktok')}
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => tiktokLogoInputRef.current?.click()}
                        className="w-full"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Custom Logo
                      </Button>
                      
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span>Size</span>
                          <span>{tiktokLogoSize}px</span>
                        </div>
                        <Slider
                          value={[tiktokLogoSize]}
                          onValueChange={(val) => setTiktokLogoSize(val[0])}
                          min={30}
                          max={120}
                          step={5}
                        />
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span>Opacity</span>
                          <span>{Math.round(tiktokLogoOpacity * 100)}%</span>
                        </div>
                        <Slider
                          value={[tiktokLogoOpacity]}
                          onValueChange={(val) => setTiktokLogoOpacity(val[0])}
                          min={0.3}
                          max={1}
                          step={0.05}
                        />
                      </div>
                      
                      <div className="text-xs text-muted-foreground text-center">
                        Position: {tiktokLogoPosition.x.toFixed(0)}%, {tiktokLogoPosition.y.toFixed(0)}%
                        <br />
                        <span className="text-primary font-medium">Drag logo on preview to reposition</span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-1">
                        <Button variant="outline" size="sm" className="text-xs h-7"
                          onClick={() => setTiktokLogoPosition({ x: 6, y: 12 })}>
                          Top Left
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs h-7"
                          onClick={() => setTiktokLogoPosition({ x: 42, y: 12 })}>
                          Top Center
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs h-7"
                          onClick={() => setTiktokLogoPosition({ x: 70, y: 12 })}>
                          Top Right
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs h-7"
                          onClick={() => setTiktokLogoPosition({ x: 6, y: 55 })}>
                          Mid Left
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs h-7"
                          onClick={() => setTiktokLogoPosition({ x: 42, y: 40 })}>
                          Center
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs h-7"
                          onClick={() => setTiktokLogoPosition({ x: 70, y: 55 })}>
                          Mid Right
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setTiktokHook("POV: You stopped counting units");
                    setTiktokSubline("$189/mo flat-rate Botox");
                    setTiktokCta("Link in bio 👇");
                    setTiktokTextColor("#eaeae0");
                    setTiktokCtaColor("#ffdb4d");
                    setTiktokShowCta(true);
                    setTiktokCustomBackground(null);
                    setTiktokVideoBackground(null);
                    setTiktokBackground(SAMPLE_BACKGROUNDS[1].url);
                    setTiktokLogoEnabled(false);
                    setTiktokLogo(null);
                    setTiktokCustomLogo(null);
                    setTiktokLogoPosition({ x: 6, y: 12 });
                    setTiktokLogoSize(60);
                    setTiktokLogoOpacity(1);
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset to Default
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Video Preview Dialog */}
        <Dialog open={showVideoPreview} onOpenChange={(open) => !open && closeVideoPreview()}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Video className="w-5 h-5" />
                Video Preview - {videoPreviewType === 'story' ? 'Instagram Story' : 'TikTok'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Video Preview */}
              <div className="space-y-4">
                <Label>Video Preview</Label>
                {videoPreviewUrl && (
                  <video
                    src={videoPreviewUrl}
                    controls
                    autoPlay
                    loop
                    className="w-full rounded-lg shadow-lg"
                    style={{ maxHeight: '400px' }}
                  />
                )}
              </div>

              {/* Thumbnail Selection */}
              <div className="space-y-4">
                <Label>Select Thumbnail</Label>
                <div className="grid grid-cols-3 gap-2">
                  {thumbnails.map((thumb, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedThumbnailIndex(index)}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                        selectedThumbnailIndex === index 
                          ? 'border-primary ring-2 ring-primary/50' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <img 
                        src={thumb} 
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full aspect-[9/16] object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs py-1 text-center">
                        {index * 2}s
                      </div>
                      {selectedThumbnailIndex === index && (
                        <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-1">
                          <Eye className="w-3 h-3" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                
                {thumbnails[selectedThumbnailIndex] && (
                  <div className="space-y-2">
                    <Label>Selected Thumbnail Preview</Label>
                    <img 
                      src={thumbnails[selectedThumbnailIndex]} 
                      alt="Selected thumbnail"
                      className="w-full max-w-[150px] rounded-lg shadow-md"
                    />
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={closeVideoPreview}>
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
              <Button variant="secondary" onClick={handleDownloadThumbnail} disabled={thumbnails.length === 0}>
                <ImageIcon className="w-4 h-4 mr-2" />
                Download Thumbnail
              </Button>
              <Button onClick={handleDownloadFromPreview}>
                <Download className="w-4 h-4 mr-2" />
                Download Video
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Save Content Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Save to Library
              </DialogTitle>
              <DialogDescription>
                Save this {saveContentType === 'story' ? 'Instagram Story' : 'TikTok'} configuration for later use.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="content-name">Name</Label>
                <Input
                  id="content-name"
                  placeholder="e.g., Summer Promo, Holiday Sale..."
                  value={saveContentName}
                  onChange={(e) => setSaveContentName(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveContent} disabled={isSaving || !saveContentName.trim()}>
                {isSaving ? 'Saving...' : 'Save to Library'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Library Dialog */}
        <Dialog open={showLibrary} onOpenChange={setShowLibrary}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Library className="w-5 h-5 text-primary" />
                Content Library
              </DialogTitle>
              <DialogDescription>
                Load saved content configurations or manage your library.
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[400px] pr-4">
              {isLoadingLibrary ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : savedContent.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Library className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No saved content yet.</p>
                  <p className="text-sm">Create a design and click "Save" to add it here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedContent.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${item.content_type === 'story' ? 'bg-pink-500/10 text-pink-500' : 'bg-blue-500/10 text-blue-500'}`}>
                          {item.content_type === 'story' ? <Instagram className="w-5 h-5" /> : <Video className="w-5 h-5" />}
                        </div>
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.content_type === 'story' ? 'Instagram Story' : 'TikTok'} • 
                            {new Date(item.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleLoadContent(item)}
                        >
                          Load
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteContent(item.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLibrary(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
